<div
  class="bg-elevated border-weak flex h-full flex-col"
  [class]="orientation() === 'horizontal' ? 'border-b' : 'border-r'">
  <!-- Main toolbar row -->
  <div
    [class]="
      orientation() === 'horizontal' ? 'flex-row items-center' : 'flex-col items-stretch'
    "
    class="flex gap-1 px-4 py-2">
    @if (showCustomContent()) {
      <ng-content select="[toolbox-lead]"></ng-content>
    }

    <!-- Search Section -->
    @if (searchConfig().active) {
      @switch (searchConfig().type) {
        @case ('simple') {
          <div
            class="border-default bg-elevated flex items-center gap-1.5 rounded-md border px-2"
            style="width: 280px">
            <i
              [class]="ICONS.magnifyingGlass"
              class="text-text-tertiary shrink-0"
              style="font-size: 12px; line-height: 1"></i>
            <input
              #filterInput
              type="text"
              class="text-text-primary placeholder:text-text-tertiary w-full border-none bg-transparent py-1 text-[12px] outline-none focus:outline-none"
              [value]="searchQuery()"
              (input)="onMainToolboxSearchChange($any($event.target).value.trim())"
              (focus)="isSearchFocused.set(true)"
              (blur)="isSearchFocused.set(false)"
              [placeholder]="searchPlaceholder()" />
          </div>
        }
        @case ('advanced') {
          <app-search-query-builder
            [class]="'border-default bg-elevated flex items-center gap-1.5 rounded-md border px-2'"
            style="width: 280px"
            [instant]="true"
            [placeholder]="searchPlaceholder()"
            [qualifiers]="searchConfig().qualifiers ?? {}"
            [queryValue]="searchQuery()"
            (searchQuery)="advancedQuerySearch.emit($event)"
            (focus)="isSearchFocused.set(true)"
            (blur)="isSearchFocused.set(false)">
          </app-search-query-builder>
        }
      }
    }

    <!-- Custom Content Slot -->
    @if (showCustomContent()) {
      <ng-content></ng-content>
    }

    <!-- Items count -->
    @if (totalItems() > 0) {
      @if (showSelect()) {
        <div
          class="group text-accent-main bg-accent-interactive border-accent-border flex cursor-pointer items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap"
          aria-haspopup="true"
          #selectionDropdownTrigger
          (click)="toggleSelectionDropdown()"
          (keydown)="toggleSelectionDropdown()"
          [attr.aria-expanded]="isSelectionDropdownOpen()">
          <div>{{ getSelectionLabel() }}</div>
          <i
            [class.rotate-180]="isSelectionDropdownOpen()"
            [class]="ICONS.caretDown"
            class="text-2xs transition-all"></i>
        </div>
        <app-dropdown
          [target]="selectionDropdownTrigger"
          [template]="selectionDropdownContent"
          [isOpen]="isSelectionDropdownOpen()"
          [onClose]="onCloseSelectionDropdown"
          [position]="'bottom-start'"
          [closeOnClickOutside]="true"
          [closeOnEscape]="true" />
      } @else {
        <span class="text-text-tertiary ml-1 shrink-0 text-xs whitespace-nowrap">
          {{ displayedItemCount() }} <span class="text-text-tertiary">sur</span>
          {{ totalItems() }}
        </span>
      }
    }

    <!-- Spacer -->
    <div class="flex-1"></div>

    <!-- Pagination -->
    @if (showPageSelector()) {
      <app-pagination
        [paginationConfig]="paginationConfig()"
        [orientation]="'horizontal'"
        (pageChange)="onPageChange($event)">
      </app-pagination>

      <span class="bg-weak mx-2 h-5 w-px shrink-0"></span>
    }

    @if (useAdvancedFiltering()) {
      <app-button
        (clickEvent)="toggleAdvancedFiltering()"
        type="icon"
        [icon]="ICONS.funnel"
        [active]="showAdvancedFiltering()"
        [tooltipPosition]="orientation() === 'horizontal' ? 'bottom' : 'right'"
        [tooltip]="'features.data.toolbox.advanced_filtering' | translate" />
    }

    @if (showPlanItems()) {
      <app-button
        (clickEvent)="onExportClick()"
        type="primary"
        [icon]="ICONS.download"
        [label]="'features.data.toolbox.add_to_plan' | translate" />
    }

    @if (!showPlanItems()) {
      <div
        [class]="orientation() === 'horizontal' ? 'flex-row' : 'flex-col'"
        class="flex h-full items-center gap-1">
        @for (section of sections(); track section.id) {
          <div
            class="flex items-center gap-1"
            [class]="section.column ? 'flex-col' : 'flex-row'">
            @for (item of section.items; track item.id) {
              @if (isDataspaceSelectorButton(item)) {
                <ng-container
                  [ngTemplateOutlet]="dataspaceSelectorButtonTemplate"
                  [ngTemplateOutletContext]="{ $implicit: item }">
                </ng-container>
              } @else if (isSelectDropdownButton(item)) {
                <ng-container
                  [ngTemplateOutlet]="selectDropdownButtonTemplate"
                  [ngTemplateOutletContext]="{ $implicit: item }">
                </ng-container>
              } @else if (isEntityRefButton(item)) {
                <ng-container
                  [ngTemplateOutlet]="entityRefButtonTemplate"
                  [ngTemplateOutletContext]="{ $implicit: item }">
                </ng-container>
              } @else if (isRegularButton(item)) {
                <span class="relative inline-flex">
                  <app-button
                    [attr.data-button-id]="item.id"
                    [disabled]="item.disabled ?? false"
                    (clickEvent)="onButtonClick(item)"
                    [type]="getButtonType(item)"
                    [label]="item.label"
                    [ariaLabel]="item.ariaLabel ?? item.label ?? item.tooltip"
                    [icon]="item.icon"
                    [active]="item.active ?? false"
                    [tooltipPosition]="orientation() === 'horizontal' ? 'bottom' : 'right'"
                    [tooltip]="item.tooltip" />
                  @if (item.indicator) {
                    <span
                      aria-hidden="true"
                      class="bg-info pointer-events-none absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"></span>
                  }
                </span>
              } @else if (isDropdownButton(item)) {
                <ng-container
                  [ngTemplateOutlet]="dropdownButtonTemplate"
                  [ngTemplateOutletContext]="{ $implicit: item }">
                </ng-container>
              }
            }
          </div>
        }
      </div>
    }
  </div>
</div>

<!-- Templates -->

<!-- Selection Dropdown Template -->
<ng-template #selectionDropdownContent>
  <app-items-dropdown [items]="getSelectionDropdownItems()" />
</ng-template>

<!-- Dropdown Button Template -->
<ng-template #dropdownButtonTemplate let-item>
  <app-button
    #dynamicDropdownTrigger
    aria-haspopup="true"
    [attr.data-dropdown-id]="item.id"
    [attr.data-button-id]="item.id"
    [attr.aria-expanded]="getDropdownOpenState(item.id)()"
    [disabled]="item.disabled ?? false"
    (clickEvent)="toggleDropdown(item.id, item.disabled)"
    [type]="getButtonType(item)"
    [label]="item.label"
    [ariaLabel]="item.ariaLabel ?? item.label ?? item.tooltip"
    [icon]="item.icon"
    [active]="getDropdownOpenState(item.id)()"
    [tooltipPosition]="orientation() === 'horizontal' ? 'bottom' : 'right'"
    [tooltip]="item.tooltip" />
  <app-dropdown
    [target]="dynamicDropdownTrigger.elementRef.nativeElement"
    [template]="dynamicDropdownContent"
    [context]="{ item: item }"
    [isOpen]="getDropdownOpenState(item.id)()"
    [onClose]="closeDropdown(item.id)"
    [position]="'bottom-start'"
    [closeOnClickOutside]="true"
    [closeOnEscape]="true" />
</ng-template>

<!-- Dataspace Selector Template -->
<ng-template #dataspaceSelectorButtonTemplate let-item>
  <div
    #dataspaceSelectorWrapper
    class="group relative flex items-center justify-center rounded">
    <app-dataspace-selector
      [value]="item.value"
      [placeholder]="item.placeholder ?? null"
      [disabled]="item.disabled ?? false"
      (valueChange)="item.onValueChange($event)" />
    @if (item.tooltip) {
      <app-tooltip
        [target]="dataspaceSelectorWrapper"
        [content]="item.tooltip"
        [position]="'bottom'" />
    }
  </div>
</ng-template>

<!-- Select Dropdown Button Template -->
<ng-template #selectDropdownButtonTemplate let-item>
  <div
    #selectDropdownWrapper
    class="group relative flex items-center justify-center rounded-md">
    <app-button-select
      [items]="item.items"
      [value]="item.value"
      (valueChange)="item.onValueChange($event)"
      (searchChange)="item.onSearchChange?.($event)"
      [enableSearch]="item.enableSearch ?? !!item.onSearchChange"
      [placeholder]="item.placeholder ?? ''"
      [searchPlaceholder]="item.searchPlaceholder ?? ''"
      [emptyMessage]="item.emptyMessage ?? ''"
      [triggerIconClass]="item.triggerIconClass ?? null"
      [disabled]="item.disabled ?? false" />
    @if (item.tooltip) {
      <app-tooltip
        [target]="selectDropdownWrapper"
        [content]="item.tooltip"
        [position]="orientation() === 'horizontal' ? 'bottom' : 'right'" />
    }
  </div>
</ng-template>

<!-- Dynamic Dropdown Content Template -->
<ng-template #dynamicDropdownContent let-item="item">
  <div class="min-w-48">
    @if (item.searchConfig) {
      <div class="mb-1 w-full">
        <input
          type="text"
          class="border-weak placeholder:text-text-tertiary hover:border-interactive hover:placeholder:text-text-secondary focus:border-accent-focus focus:ring-accent-focus h-7 w-full rounded border px-3 py-1 text-sm focus:ring-2 focus:outline-none"
          [placeholder]="item.searchConfig.placeholder"
          [value]="getDropdownSearchTerm(item.id)()"
          (input)="onDropdownSearchTermChange($any($event.target).value, item.id)" />
      </div>
    }
    <div class="g-menu-scroll">
      <app-items-dropdown [items]="getFilteredDropdownItems(item.id, item.items)" />
    </div>
  </div>
</ng-template>

<ng-template #entityRefButtonTemplate let-item>
  <app-button
    #entityRefTrigger
    aria-haspopup="true"
    [attr.data-dropdown-id]="item.id"
    [attr.data-button-id]="item.id"
    [attr.aria-expanded]="getDropdownOpenState(item.id)()"
    [disabled]="item.disabled ?? false"
    (clickEvent)="toggleDropdown(item.id, item.disabled)"
    [type]="getButtonType(item)"
    [label]="item.label"
    [icon]="item.icon"
    [active]="getDropdownOpenState(item.id)()"
    [tooltipPosition]="orientation() === 'horizontal' ? 'bottom' : 'right'"
    [tooltip]="item.tooltip" />
  <app-dropdown
    [target]="entityRefTrigger.elementRef.nativeElement"
    [template]="entityRefDropdownContent"
    [context]="{ item: item }"
    [isOpen]="getDropdownOpenState(item.id)()"
    [onClose]="closeDropdown(item.id)"
    [position]="'bottom-start'"
    [useDefaultStyles]="false"
    [closeOnClickOutside]="true"
    [closeOnEscape]="true" />
</ng-template>

<!-- The picker renders the whole panel, so the button opens one dropdown rather than a
     popover nested inside another. -->
<ng-template #entityRefDropdownContent let-item="item">
  <app-entity-auto-complete
    [entityType]="item.entityType"
    [isArray]="false"
    [inline]="true"
    [allowClear]="!!item.onClear"
    [value]="null"
    (valueChange)="onEntityRefPick(item, $event)"
    (cleared)="onEntityRefClear(item)" />
</ng-template>
